import {
  BehaviorSubject,
  Subscription,
  Subject,
  Observable,
  Observer,
} from 'rxjs'
import { AnonymousSubject } from 'rxjs/internal/Subject'
import { takeUntil, map, first } from 'rxjs/operators'

export interface WrappedSocketConfig {
  url: string
  autoConnect?: boolean
  protocol?: string | string[]
}

export enum WrappedSocketEventTypes {
  CONNECT = 'CONNECT',
  DISCONNECT = 'DISCONNECT',
  MESSAGE = 'MESSAGE',
  ERROR = 'ERROR',
}

export interface WrappedSocketEvent {
  type: WrappedSocketEventTypes
  data: any
}

export class WrappedSocket {
  //@ts-ignore
  private wsSubject: AnonymousSubject<MessageEvent>
  //@ts-ignore
  private readonly eventSubject: BehaviorSubject<WrappedSocketEvent> =
    new BehaviorSubject(null)
  private readonly eventMap: Map<WrappedSocketEventTypes, Subscription> =
    new Map()
  private readonly callbackMap: Map<WrappedSocketEventTypes, any[]> = new Map()
  private readonly killer: Subject<boolean> = new Subject()
  private readonly connected: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  )

  constructor(private readonly config: WrappedSocketConfig) {
    if (config.autoConnect) {
      this.connect()
    }
  }

  public connect(force = false): void {
    if (
      ((!this.wsSubject || this.wsSubject.closed) && this.config.url) ||
      force
    ) {
      const ws = new WebSocket(this.config.url, this.config.protocol)
      const observable = new Observable((obs: Observer<MessageEvent>) => {
        ws.onmessage = obs.next.bind(obs)
        ws.onerror = obs.error.bind(obs)
        ws.onclose = obs.complete.bind(obs)
        return ws.close.bind(ws)
      })
      ws.onopen = () => {
        this.setConnectionStatus(true)
        this.setMessageListener()

        setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('heartbeat')
          }
        }, 30000)
      }
      const observer = {
        error: (err: any) => {
          this.eventSubject.next({
            type: WrappedSocketEventTypes.ERROR,
            data: err,
          })
        },
        complete: () => {
          this.setConnectionStatus(false)
          ws.close()

          // Try to reconnect
          setTimeout(() => {
            this.connect(true)
          }, 300)
        },
        next: (data: any) => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(data)
          }
        },
      }

      this.wsSubject = new AnonymousSubject<MessageEvent>(observer, observable)
    }
  }

  public async send(data: any): Promise<void> {
    try {
      await this.socketReady()
      if (typeof data !== 'string') {
        data = JSON.stringify(data)
      }

      this.wsSubject.next(data)
    } catch (error) {
      console.error('WebSocket send error:', error)
      throw error
    }
  }

  public disconnect(): void {
    this.wsSubject.complete()
  }

  public on(type: WrappedSocketEventTypes, cb: (d?: any) => void): void {
    const cbMap = this.callbackMap.get(type)
    if (cbMap) {
      cbMap.push(cb)
    } else {
      this.callbackMap.set(type, [cb])
    }

    const topical = this.eventMap.get(type)
    if (!topical) {
      const sub = this.eventSubject
        .asObservable()
        .pipe(takeUntil(this.killer))
        .subscribe((evt: WrappedSocketEvent) => {
          if (evt?.type === type) {
            this.callbackMap.get(type)?.forEach((c) => c(evt.data))
          }
        })
      this.eventMap.set(type, sub)
    }
  }

  public removeListener(topic: WrappedSocketEventTypes): void {
    const topical = this.eventMap.get(topic)
    if (topical) {
      topical.unsubscribe()
      this.eventMap.delete(topic)
      this.callbackMap.delete(topic)
    }
  }

  public removeAllListeners(): void {
    Array.from(this.eventMap.keys()).forEach((k) => this.removeListener(k))
  }

  public isConnected(): boolean {
    return this.connected.value
  }

  private parseSocketData(data: any): any {
    try {
      data = JSON.parse(data)
    } catch (err) {}
    return data?.data || data
  }

  private setMessageListener(): void {
    this.wsSubject
      .pipe(
        takeUntil(this.killer),
        map((r) => this.parseSocketData(r?.data || r)),
      )
      .subscribe((data: any) => {
        this.eventSubject.next({ type: WrappedSocketEventTypes.MESSAGE, data })
      })
  }

  private setConnectionStatus(connected: boolean): void {
    this.connected.next(connected)
    this.eventSubject.next({
      type: connected
        ? WrappedSocketEventTypes.CONNECT
        : WrappedSocketEventTypes.DISCONNECT,
      data: null,
    })
  }

  private socketReady(): Promise<boolean | undefined> {
    /* tslint:disable-next-line:no-boolean-literal-compare */
    return this.connected
      .asObservable()
      .pipe(first((r) => r !== false))
      .toPromise()
  }
}
